import { differenceInHours, differenceInYears, fromUnixTime, getUnixTime } from 'date-fns';
import { proxy } from 'valtio';
import { z } from 'zod';
import { getManyFromList } from '@soundxyz/utils';
import {
  AllowlistSourceType,
  AuctionTypeEnum,
  createEnum,
  enumChecker,
  MULTI_SELECT_ENTITIY_LIMIT,
} from '@soundxyz/utils/const';
import {
  containsDecimal,
  getDateTimeErrors,
  getTimingErrors,
  isDefined,
  isParseableNumeric,
  transformStringFloat,
  transformStringInteger,
} from '@components/Dashboard/Upload/helpers';
import { PRESALE_MIN_SUPPLY, SAM_DEFAULT_ENABLED } from '@utils/constants';
import { hasMoreThanXDecimals, MAX_UPLOAD_RELEASE_PRICE_DECIMALS } from '@utils/helpers';
import { EditErrorMessages } from './edit/errors';
import {
  AuctionConfigurationErrors,
  decimalsError,
  ErrorMessages,
  initialAuctionConfigurationErrors,
  MAX_CHAR_LENGTH,
} from './errors';

const numericNullish = z
  .union([z.string(), z.number()])
  .transform(v => {
    const stringNum = v.toString();
    return stringNum ? parseInt(stringNum, 10) : null;
  })
  .nullish();

const numericString = z.number().nullish().or(z.string());

export type CollectorsOfReleasesKeys = keyof CollectorsOfReleases;
export type CollectorsOfArtistsKeys = keyof CollectorsOfArtists;

const CollectorsOfReleasesSchema = z
  .object({
    allArtistReleases: z.boolean(),
    allArtistCollaborations: z.boolean(),
    selectedReleaseIds: z.array(z.string()),
  })
  .nullish()
  .transform(o => {
    return {
      allArtistReleases: o?.allArtistReleases ?? false,
      allArtistCollaborations: o?.allArtistCollaborations ?? false,
      selectedReleaseIds: o?.selectedReleaseIds ?? [],
    };
  });

export const CollectorsOfArtistsSchemaShared = z.object({
  allArtistCollectors: z.boolean(),
  allCollaboratingArtists: z.boolean(),
  selectedArtistIds: z.array(z.string()),
});

const CollectorsOfArtistsSchema = CollectorsOfArtistsSchemaShared.nullish().transform(o => {
  return {
    allArtistCollectors: o?.allArtistCollectors ?? true,
    allCollaboratingArtists: o?.allCollaboratingArtists ?? false,
    selectedArtistIds: o?.selectedArtistIds ?? [],
  };
});

export type CollectorsOfArtists = z.infer<typeof CollectorsOfArtistsSchema>;
export type CollectorsOfReleases = z.infer<typeof CollectorsOfReleasesSchema>;

export const ManuallyAddedAllowlistSchema = z.object({
  description: z
    .string()
    .nullish()
    .superRefine((value, ctx) => {
      if (value && value.length > MAX_CHAR_LENGTH['manuallyAddedDescription']) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: EditErrorMessages.manuallyAddedAllowlist?.description_too_long,
          path: ['description'],
        });
      }
      return value;
    }),
  hasAddresses: z.boolean().nullish(),
  addresses: z.array(z.string()).nullish(),
});

export type ManuallyAddedAllowlist = (typeof ManuallyAddedAllowlistSchema)['_output'];

export type ManuallyAddedAllowlistKeys = keyof ManuallyAddedAllowlist;

const UnvalidatableFields = z.object({
  auctionType: z.nativeEnum(AuctionTypeEnum),
  enabledSAM: z.boolean().default(SAM_DEFAULT_ENABLED),
  presaleAllowList: z
    .array(
      z.object({
        address: z.string(),
        source: z.nativeEnum(AllowlistSourceType),
      }),
    )
    .nullish()
    .transform(v => v ?? []),
});

export const AuctionConfigValidatableFields = z
  .object({
    maxMintable: numericString.transform(transformStringInteger).superRefine((value, ctx) => {
      const isAlbum = auctionConfigurationState.isAlbum;

      // If Album or Fixed Edition AND is not parseable, throw error
      if (
        auctionConfigurationState.fields.auctionType === AuctionTypeEnum.FIXED_QUANTITY ||
        auctionConfigurationState.isAlbum
      ) {
        if (!value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: isAlbum
              ? ErrorMessages.albumMintSupply.required_error
              : ErrorMessages.maxMintable.required_error,
          });
          return z.NEVER;
        }
      }

      // Throw error if number has a decimal
      if (value != null && containsDecimal(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Total Mint Supply cannot have a decimal',
        });
        return z.NEVER;
      }

      // Total mint supply must be within min and max quantity limits
      const minQuantity = auctionConfigurationState.mintQuantityLimit.minQuantity;
      const maxQuantity = auctionConfigurationState.mintQuantityLimit.maxQuantity;

      if (value != null && minQuantity != null && maxQuantity != null) {
        if (value < minQuantity || value > maxQuantity) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Value must be between ${minQuantity} and ${maxQuantity}`,
          });
          return z.NEVER;
        }
      }
    }),
    maxQuantity: numericNullish,
    minQuantity: numericNullish,
    presaleMaxMintsPerWallet: numericString
      .transform(transformStringInteger)
      .superRefine((value, ctx) => {
        if (!auctionConfigurationState.hasPresaleMint) return;

        if (!value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.presaleMaxMintsPerWallet.required_error,
          });
          return z.NEVER;
        }

        const minQuantity = auctionConfigurationState.mintQuantityLimit.minPresaleMaxMintsPerWallet;
        const maxQuantity = auctionConfigurationState.mintQuantityLimit.maxPresaleMaxMintsPerWallet;

        if (minQuantity != null && maxQuantity != null) {
          if (value < minQuantity || value > maxQuantity) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Value must be between ${minQuantity} and ${maxQuantity}`,
            });
          }
        }
      }),
    presaleMaxSupply: numericString.transform(transformStringInteger).superRefine((value, ctx) => {
      if (!auctionConfigurationState.hasPresaleMint) return;

      if (!value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.presaleMaxSupply.required_error,
        });
        return z.NEVER;
      }

      if (containsDecimal(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Presale Max Supply cannot have a decimal',
        });
        return z.NEVER;
      }

      // Presale max supply must be within min and max quantity limits
      const minQuantity = PRESALE_MIN_SUPPLY;
      const maxQuantity = transformStringInteger(auctionConfigurationState.fields.maxMintable);

      if (maxQuantity != null) {
        if (value < minQuantity || value > maxQuantity) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Value must be between ${minQuantity} and ${maxQuantity}`,
          });
          return z.NEVER;
        }
      }
    }),
    presalePricePerMint: numericString.transform(transformStringFloat).superRefine((value, ctx) => {
      if (
        value != null &&
        hasMoreThanXDecimals({
          value,
          maxDecimals: MAX_UPLOAD_RELEASE_PRICE_DECIMALS,
        })
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: decimalsError,
        });
        return z.NEVER;
      }
    }),
    presaleStartTime: z
      .date()
      .nullable()
      .superRefine((value, ctx) => {
        if (!isDefined(value) && auctionConfigurationState.hasPresaleMint) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.presaleStartTime.required_error,
          });
          return z.NEVER;
        }
      }),
    presaleEndTime: z
      .date()
      .nullable()
      .superRefine((value, ctx) => {
        if (!isDefined(value) && auctionConfigurationState.hasPresaleMint) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.presaleEndTime.required_error,
          });
          return z.NEVER;
        }

        const presaleStartTime = auctionConfigurationState.fields.presaleStartTime;

        if (value && presaleStartTime && differenceInHours(value, presaleStartTime) < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Must be at least an hour after the presale start time',
            path: ['presaleEndTime'],
          });
          return z.NEVER;
        }

        if (value && presaleStartTime && differenceInYears(value, presaleStartTime) >= 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Must be within one year of start time',
            path: ['presaleEndTime'],
          });
          return z.NEVER;
        }

        // for open editions (SAM and non SAM), we want to enforce that presaleEndTime <= publicSaleEndTime
        if (
          auctionConfigurationState.fields.auctionType === AuctionTypeEnum.OPEN_EDITION ||
          auctionConfigurationState.fields.auctionType === AuctionTypeEnum.OPEN_EDITION_WITH_SAM
        ) {
          const publicEndTime = auctionConfigurationState.fields.publicEndTime;

          if (value && publicEndTime && value > publicEndTime) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Must be before than public sale end time',
              path: ['presaleEndTime'],
            });
            return z.NEVER;
          }
        }
      }),
    publicEndTime: z
      .date()
      .nullable()
      /**
       * need to do transform before refine / superRefine because we are not showing publicEndTime for fixedQuantity
       */
      .transform(value => {
        const publicStartTime = auctionConfigurationState.fields.publicStartTime;
        /**
         * public end time for fixed edition needs to be greater than startTime,
         * so we are hard setting it to publicStartTime + 1
         */
        if (
          publicStartTime &&
          auctionConfigurationState.fields.auctionType === AuctionTypeEnum.FIXED_QUANTITY
        ) {
          const data = fromUnixTime(getUnixTime(publicStartTime) + 1);
          return data;
        }

        return value;
      })
      .refine(isDefined, {
        message: ErrorMessages.publicEndTime.required_error,
      })
      .superRefine((value, ctx) => {
        const publicStartTime = auctionConfigurationState.fields.publicStartTime;

        if (
          publicStartTime &&
          differenceInHours(value, publicStartTime) < 1 &&
          auctionConfigurationState.fields.auctionType !== AuctionTypeEnum.FIXED_QUANTITY
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Must be at least an hour after the public start time',
            path: ['publicEndtime'],
          });
          return z.NEVER;
        }

        if (publicStartTime && differenceInYears(value, publicStartTime) >= 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Must be within one year of start time',
            path: ['publicEndTime'],
          });
          return z.NEVER;
        }
      }),
    publicMaxMintsPerWallet: numericString
      .transform(transformStringInteger)
      .refine(isParseableNumeric, {
        message: ErrorMessages.publicMaxMintsPerWallet.required_error,
      })
      .superRefine((value, ctx) => {
        const minQuantity =
          auctionConfigurationState.mintQuantityLimit.minPublicSaleMaxMintsPerWallet;
        const maxQuantity =
          auctionConfigurationState.mintQuantityLimit.maxPublicSaleMaxMintsPerWallet;

        const mintsPerWalletError =
          (minQuantity && Number(value) < minQuantity) ||
          (maxQuantity && Number(value) > maxQuantity)
            ? `Value must be between ${minQuantity} and ${maxQuantity}`
            : null;

        if (mintsPerWalletError) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: mintsPerWalletError,
          });
        }
      }),
    publicPricePerMint: numericString
      .transform(transformStringFloat)
      .superRefine((value, ctx) => {
        if (
          value != null &&
          hasMoreThanXDecimals({
            value,
            maxDecimals: MAX_UPLOAD_RELEASE_PRICE_DECIMALS,
          })
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: decimalsError,
          });
          return z.NEVER;
        }
      })
      .refine(isDefined, {
        message: ErrorMessages.publicPricePerMint.required_error,
      }),
    publicStartTime: z.date().nullable().refine(isDefined, {
      message: ErrorMessages.publicStartTime.required_error,
    }),
    quantityBreakdown: z.array(
      numericString.transform(v => (v != null ? parseInt(v.toString()) : 0)),
    ),
    collectorsOfReleases: z
      .object({
        allArtistReleases: z.boolean(),
        allArtistCollaborations: z.boolean(),
        selectedReleaseIds: z.array(z.string()),
      })
      .nullish()
      .transform(value => {
        if (!auctionConfigurationState.hasPresaleMint) return value;

        return {
          allArtistReleases: value?.allArtistReleases ?? false,
          allArtistCollaborations: value?.allArtistCollaborations ?? false,
          selectedReleaseIds: value?.selectedReleaseIds ?? [],
        };
      }),
    collectorsOfArtists: z
      .object({
        allArtistCollectors: z.boolean(),
        allCollaboratingArtists: z.boolean(),
        selectedArtistIds: z.array(z.string()),
      })
      .nullish()
      .transform(value => {
        if (!auctionConfigurationState.hasPresaleMint) return value;

        return {
          allArtistCollectors: value?.allArtistCollectors ?? true,
          allCollaboratingArtists: value?.allCollaboratingArtists ?? false,
          selectedArtistIds: value?.selectedArtistIds ?? [],
        };
      }),
    manuallyAddedAllowlist: z
      .object({
        description: z
          .string()
          .nullish()
          .transform((value, ctx) => {
            if (!auctionConfigurationState.hasPresaleMint) return value;

            if (value && value.length > MAX_CHAR_LENGTH['manuallyAddedDescription']) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: ErrorMessages.manuallyAddedAllowlist?.description_too_long,
                path: ['description'],
              });
            }
            return value;
          }),
        hasAddresses: z.boolean().nullish(),
      })
      .nullish(),
  })
  .superRefine((values, ctx) => {
    const hasManuallyAddedAddresses = values.manuallyAddedAllowlist?.hasAddresses;
    const hasCollectorsOfReleases =
      (values.collectorsOfReleases?.selectedReleaseIds
        ? values.collectorsOfReleases?.selectedReleaseIds.length > 0
        : false) ||
      values.collectorsOfReleases?.allArtistCollaborations ||
      values.collectorsOfReleases?.allArtistReleases;
    const hasCollectorsOfArtists =
      (values.collectorsOfArtists?.selectedArtistIds
        ? values.collectorsOfArtists?.selectedArtistIds.length > 0
        : false) || values.collectorsOfArtists?.allArtistCollectors;

    if (
      auctionConfigurationState.hasPresaleMint &&
      !hasManuallyAddedAddresses &&
      !hasCollectorsOfReleases &&
      !hasCollectorsOfArtists
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: ErrorMessages.addresses.required_error,
        path: ['addresses'],
      });
      return;
    }

    if (
      values.collectorsOfReleases?.selectedReleaseIds &&
      values.collectorsOfReleases.selectedReleaseIds.length > MULTI_SELECT_ENTITIY_LIMIT
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: ErrorMessages.collectorsOfReleases.list_too_long,
        path: ['collectorsOfReleases'],
      });
    }

    if (
      values.collectorsOfArtists?.selectedArtistIds &&
      values.collectorsOfArtists.selectedArtistIds.length > MULTI_SELECT_ENTITIY_LIMIT
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: ErrorMessages.collectorsOfArtists.list_too_long,
        path: ['collectorsOfArtists'],
      });
    }

    const { public: publicTimingErrors } = getDateTimeErrors({
      startTime: values.publicStartTime,
      endTime: values.publicEndTime,
      type: 'public',
    });

    const { presale: presaleTimingErrors } = getDateTimeErrors({
      startTime: values.presaleStartTime,
      endTime: values.presaleEndTime,
      type: 'presale',
    });

    if (publicTimingErrors) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: publicTimingErrors.error,
        path: [publicTimingErrors.path],
        fatal: true,
      });
    }

    if (presaleTimingErrors && auctionConfigurationState.hasPresaleMint) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: presaleTimingErrors.error,
        path: [presaleTimingErrors.path],
        fatal: true,
      });
    }

    if (
      values.presaleStartTime &&
      values.publicStartTime &&
      values.presaleStartTime > values.publicStartTime
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Public sale must be at the same time or after presale',
        path: ['publicStartTime'],
      });
    }

    // Presale price must be less or equal than Public price only if Has Presale Mint is true
    if (
      typeof values.presalePricePerMint === 'number' &&
      typeof values.publicPricePerMint === 'number' &&
      values.presalePricePerMint > values.publicPricePerMint &&
      auctionConfigurationState.hasPresaleMint
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Must be less than or equal to your public sale price of ${values.publicPricePerMint} ETH`,
        path: ['presalePricePerMint'],
      });
      return z.NEVER;
    }

    // Public price must be within price limits
    const publicMaxPrice = auctionConfigurationState.priceLimit.publicMaxPrice;
    const publicMinPrice = auctionConfigurationState.priceLimit.publicMinPrice;

    const publicPriceRangeError =
      publicMinPrice === publicMaxPrice
        ? `Value must be ${publicMinPrice}`
        : `Value must be between ${publicMinPrice} and ${publicMaxPrice}`;

    if (values.publicPricePerMint == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: publicPriceRangeError,
        path: ['publicPricePerMint'],
      });
      return z.NEVER;
    }

    if (publicMaxPrice != null && publicMinPrice != null) {
      if (
        (typeof values.publicPricePerMint === 'number' &&
          (values.publicPricePerMint < publicMinPrice ||
            values.publicPricePerMint > publicMaxPrice)) ||
        Number.isNaN(values.publicPricePerMint)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: publicPriceRangeError,
          path: ['publicPricePerMint'],
        });
        return z.NEVER;
      }
    }

    // Presale price must be within price limits
    const presaleMaxPrice = auctionConfigurationState.priceLimit.presaleMaxPrice;
    const presaleMinPrice = auctionConfigurationState.priceLimit.presaleMinPrice;

    const presalePriceRangeError =
      presaleMinPrice === presaleMaxPrice
        ? `Value must be ${presaleMinPrice}`
        : `Value must be between ${presaleMinPrice} and ${presaleMaxPrice}`;

    if (values.presalePricePerMint == null && auctionConfigurationState.hasPresaleMint) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: presalePriceRangeError,
        path: ['presalePricePerMint'],
      });
      return z.NEVER;
    }

    if (
      presaleMaxPrice != null &&
      presaleMinPrice != null &&
      auctionConfigurationState.hasPresaleMint &&
      typeof values.presalePricePerMint === 'number'
    ) {
      if (
        values.presalePricePerMint < presaleMinPrice ||
        values.presalePricePerMint > presaleMaxPrice ||
        Number.isNaN(values.presalePricePerMint)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: presalePriceRangeError,
          path: ['presalePricePerMint'],
        });
        return z.NEVER;
      }
    }

    const timingErrors = getTimingErrors({
      presaleStartTime: values.presaleStartTime,
      publicStartTime: values.publicStartTime,
    });

    if (timingErrors.presale) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: timingErrors.presale,
        path: ['presaleStartTime'],
      });
      return z.NEVER;
    }

    if (
      typeof values.maxMintable === 'number' &&
      values.quantityBreakdown &&
      auctionConfigurationState.isAlbum
    ) {
      const total = values.quantityBreakdown.reduce((prev, curr) => prev + curr, 0);

      if (total !== values.maxMintable) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.quantityBreakdown.total_mismatch,
          path: ['quantityBreakdown'],
        });
        return z.NEVER;
      }
    }
  });

// Here you can create more complex rules, having access to all values in Schema
const AuctionConfigurationFields = AuctionConfigValidatableFields.and(UnvalidatableFields);

export type AuctionConfigurationFieldsKeys = keyof z.infer<typeof AuctionConfigurationFields>;

export const AuctionConfigurationPriceLimits = z.object({
  publicMinPrice: z.number().optional(),
  publicMaxPrice: z.number().optional(),
  presaleMinPrice: z.number().optional(),
  presaleMaxPrice: z.number().optional(),
});

export const AuctionConfigurationMintQuantityLimits = z.object({
  minQuantity: z.number().optional(),
  maxQuantity: z.number().optional(),
  minPublicSaleMaxMintsPerWallet: z.number().optional(),
  maxPublicSaleMaxMintsPerWallet: z.number().optional(),
  minPresaleMaxMintsPerWallet: z.number().optional(),
  maxPresaleMaxMintsPerWallet: z.number().optional(),
});

export type AuctionConfigurationPriceLimits = (typeof AuctionConfigurationPriceLimits)['_input'];

export type AuctionConfigurationMintQuantityLimits =
  (typeof AuctionConfigurationMintQuantityLimits)['_input'];

type AuctionConfigurationFields = (typeof AuctionConfigurationFields)['_input'];

const AuctionConfigurationFieldOptions =
  AuctionConfigValidatableFields._def['schema'].keyof().options;

export type AuctionConfigurationFieldOptionsType =
  (typeof AuctionConfigurationFieldOptions)[number];

const AuctionConfigUnnestedKeys = createEnum(
  getManyFromList(AuctionConfigurationFieldOptions, v =>
    v !== 'collectorsOfArtists' && v !== 'collectorsOfReleases' && v !== 'manuallyAddedAllowlist'
      ? v
      : null,
  ),
);
export const isAuctionConfigUnnestedFieldKeysType = enumChecker(AuctionConfigUnnestedKeys);

const initialAuctionConfigurationFields = () =>
  ({
    auctionType: 'OPEN_EDITION',
    enabledSAM: SAM_DEFAULT_ENABLED,
    maxMintable: null,
    maxQuantity: null,
    minQuantity: null,
    presaleAllowList: null,
    presaleMaxMintsPerWallet: null,
    presaleMaxSupply: null,
    presalePricePerMint: null,
    presaleStartTime: null,
    presaleEndTime: null,
    publicEndTime: null,
    publicMaxMintsPerWallet: null,
    publicPricePerMint: null,
    publicStartTime: null,
    quantityBreakdown: [],
    collectorsOfReleases: {
      selectedReleaseIds: [],
      allArtistCollaborations: false,
      allArtistReleases: false,
    },
    collectorsOfArtists: {
      allArtistCollectors: true,
      selectedArtistIds: [],
      allCollaboratingArtists: false,
    },
    manuallyAddedAllowlist: {
      description: null,
      hasAddresses: null,
    },
  }) satisfies AuctionConfigurationFields;

export const initialAuctionConfigurationPriceLimits = () =>
  ({
    presaleMaxPrice: undefined,
    presaleMinPrice: undefined,
    publicMaxPrice: undefined,
    publicMinPrice: undefined,
  }) satisfies AuctionConfigurationPriceLimits;

export const initialAuctionConfigurationMintQuantityLimits = () =>
  ({
    minQuantity: undefined,
    maxQuantity: undefined,
    minPublicSaleMaxMintsPerWallet: undefined,
    maxPublicSaleMaxMintsPerWallet: undefined,
    minPresaleMaxMintsPerWallet: undefined,
    maxPresaleMaxMintsPerWallet: undefined,
  }) satisfies AuctionConfigurationMintQuantityLimits;

export const resetAuctionConfigurationState = () => {
  auctionConfigurationState.fields = initialAuctionConfigurationFields();
  auctionConfigurationState.errors = initialAuctionConfigurationErrors();
  auctionConfigurationState.hasPresaleMint = false;
  auctionConfigurationState.isValid = false;
  auctionConfigurationState.priceLimit = initialAuctionConfigurationPriceLimits();
  auctionConfigurationState.mintQuantityLimit = initialAuctionConfigurationMintQuantityLimits();
  auctionConfigurationState.isAlbum = false;
  auctionConfigurationState.touched = initialAuctionConfigurationTouchedFields();
};

const AuctionConfigurationTouchedFields = z.object({
  maxMintable: z.boolean(),
  maxQuantity: z.boolean(),
  minQuantity: z.boolean(),
  presaleMaxMintsPerWallet: z.boolean(),
  presaleMaxSupply: z.boolean(),
  presalePricePerMint: z.boolean(),
  presaleStartTime: z.boolean(),
  presaleEndTime: z.boolean(),
  publicEndTime: z.boolean(),
  publicMaxMintsPerWallet: z.boolean(),
  publicPricePerMint: z.boolean(),
  publicStartTime: z.boolean(),
  quantityBreakdown: z.boolean(),
  collectorsOfReleases: z.boolean(),
  collectorsOfArtists: z.boolean(),
  manuallyAddedAllowlist: z.object({
    description: z.boolean(),
    hasAddresses: z.boolean(),
  }),
});

type AuctionConfigurationTouchedFields = (typeof AuctionConfigurationTouchedFields)['_input'];

const initialAuctionConfigurationTouchedFields = () =>
  ({
    maxMintable: false,
    maxQuantity: false,
    minQuantity: false,
    presaleMaxMintsPerWallet: false,
    presaleMaxSupply: false,
    presalePricePerMint: false,
    presaleStartTime: false,
    presaleEndTime: false,
    publicEndTime: false,
    publicMaxMintsPerWallet: false,
    publicPricePerMint: false,
    publicStartTime: false,
    quantityBreakdown: false,
    collectorsOfReleases: false,
    collectorsOfArtists: false,
    manuallyAddedAllowlist: {
      description: false,
      hasAddresses: false,
    },
  }) satisfies AuctionConfigurationTouchedFields;

const AuctionConfigurationSchema = z.object({
  fields: AuctionConfigurationFields,
  touched: AuctionConfigurationTouchedFields,
  errors: AuctionConfigurationErrors,
  hasPresaleMint: z.boolean(),
  isValid: z.boolean(),
  priceLimit: AuctionConfigurationPriceLimits,
  mintQuantityLimit: AuctionConfigurationMintQuantityLimits,
  isAlbum: z.boolean(),
});

type AuctionConfigurationState = (typeof AuctionConfigurationSchema)['_input'];

export const auctionConfigurationState: AuctionConfigurationState = proxy({
  errors: initialAuctionConfigurationErrors(),
  fields: initialAuctionConfigurationFields(),
  touched: initialAuctionConfigurationTouchedFields(),
  hasPresaleMint: false,
  isValid: false,
  priceLimit: initialAuctionConfigurationPriceLimits(),
  isAlbum: false,
  mintQuantityLimit: initialAuctionConfigurationMintQuantityLimits(),
});
